import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { ColorRing } from "react-loader-spinner";

import mainLogo from "../logo.png";
import { otpSchemaSignup } from "../schemas";

// Same-origin base (proxied to auth-backend) — matches authActions.login so the
// `mv_otp_verified` cookie is set first-party, exactly like `refreshToken`.
const authAPIEndpoint = "/api/auth";

/**
 * Field-app MFA step. Rendered by <Login/> after a field-app password login
 * (or an OTP-only re-entry for a live session). Sends an SMS OTP via the
 * otp-verification lambda (`type: "login"`), verifies it, exchanges the
 * returned MFA ticket at auth-backend for the proof cookie, then redirects
 * back to the field app.
 *
 * Props:
 *  - phone: 10-digit registered mobile of the authenticated user
 *  - appName: resolved app name (always "web:field" here)
 *  - mfaData: { redirectUri, isLocalhostDev, tokens? } — how to finish the redirect
 *  - onBack: return to the normal login screen
 */
function MfaOtp({ phone, appName, mfaData, onBack }) {
  const [confirmCode, setConfirmCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [minutes, setMinutes] = useState(2);
  const [seconds, setSeconds] = useState(59);
  const sentRef = useRef(false);

  const username = `+91${phone}`;

  const formikOTP = useFormik({
    initialValues: { confirmCode: "" },
    validationSchema: otpSchemaSignup,
    validateOnMount: true,
  });

  // Countdown for the resend timer.
  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds((s) => s - 1);
      } else if (minutes > 0) {
        setMinutes((m) => m - 1);
        setSeconds(59);
      } else {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds, minutes]);

  const sendOtp = async () => {
    setErrorMessage("");
    try {
      await axios.post(`${process.env.REACT_APP_OTP_URL}/verify-otp`, {
        request: "true",
        username,
        type: "login",
        appName,
      });
      setMinutes(2);
      setSeconds(59);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          "Failed to send OTP. Please try again."
      );
    }
  };

  // Send an OTP as soon as the step opens (guarded so React strict-mode /
  // re-renders don't fire duplicate SMS).
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishRedirect = () => {
    const { redirectUri, isLocalhostDev, tokens } = mfaData || {};
    if (!redirectUri) {
      window.location.href = "/";
      return;
    }
    if (isLocalhostDev && tokens) {
      window.location.href =
        `${redirectUri}?token=${tokens.token}` +
        `&idToken=${tokens.idToken}&refreshToken=${tokens.refreshToken}`;
    } else {
      window.location.href = redirectUri;
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      // 1. Verify the OTP with the lambda → receive a short-lived MFA ticket.
      const verifyRes = await axios.post(
        `${process.env.REACT_APP_OTP_URL}/verify-otp`,
        {
          verify: "true",
          otp: confirmCode,
          username,
          type: "login",
        }
      );

      const mfaTicket = verifyRes?.data?.mfaTicket;
      if (!mfaTicket) {
        throw new Error("MFA verification failed. Please try again.");
      }

      // 2. Exchange the ticket for the httpOnly proof cookie (set by auth-backend).
      await axios.post(
        `${authAPIEndpoint}/otp/field-confirm`,
        { mfaTicket },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`
            )}`,
          },
          withCredentials: true,
        }
      );

      // 3. Proof set — return to the field app.
      finishRedirect();
    } catch (err) {
      setLoading(false);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid OTP. Please try again."
      );
    }
  };

  return (
    <form className="sign-up-form" onSubmit={verifyOtp}>
      <img src={mainLogo} alt="Logo" className="logo" />
      <br />
      <h3>Verify OTP</h3>
      <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
        Enter the OTP sent to your registered mobile number ending in{" "}
        {phone ? `****${String(phone).slice(-4)}` : ""}.
      </p>
      <div className="input-field1">
        <i className="fa fa-lock"></i>
        <span className="contryCode"></span>
        <input
          maxLength={6}
          type="text"
          placeholder="OTP"
          name="confirmCode"
          value={confirmCode}
          onBlur={formikOTP.handleBlur}
          onChange={(e) => {
            setConfirmCode(e.target.value);
            formikOTP.setFieldValue("confirmCode", e.target.value);
          }}
          onKeyPress={(e) => {
            const charCode = e.which ? e.which : e.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
              e.preventDefault();
            }
          }}
        />
      </div>
      {formikOTP.touched.confirmCode && formikOTP.errors.confirmCode ? (
        <div className="error-message">{formikOTP.errors.confirmCode}</div>
      ) : null}{" "}
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {loading ? (
        <div>
          <center>
            <p>Verifying OTP please wait..!</p>
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="blocks-loading"
              wrapperStyle={{}}
              wrapperClass="blocks-wrapper"
              colors={["#3347c5", "#331492", "#ff0000"]}
            />
          </center>
        </div>
      ) : (
        <button
          type="submit"
          onClick={verifyOtp}
          className={formikOTP.isValid ? "btn solid " : "loginButton"}
          disabled={!formikOTP.isValid}
        >
          Verify OTP
        </button>
      )}
      {seconds > 0 || minutes > 0 ? (
        <p>
          Time Remaining: {minutes < 10 ? `0${minutes}` : minutes}:
          {seconds < 10 ? `0${seconds}` : seconds}
        </p>
      ) : (
        <p>Didn't recieve code?</p>
      )}
      <button
        type="button"
        disabled={seconds > 0 || minutes > 0}
        style={{
          color: seconds > 0 || minutes > 0 ? "#DFE3E8" : "rgb(0, 0, 205)",
        }}
        onClick={sendOtp}
        className="link-button"
      >
        Resend OTP
      </button>
      {onBack && (
        <button type="button" onClick={onBack} className="link-button">
          Go back to Sign In
        </button>
      )}
    </form>
  );
}

export default MfaOtp;
